import type { DrawingEntity } from '../contract/types.js';

/**
 * Geometric validity — PRD Finding 2 made concrete.
 *
 * A DXF library's audit function checks *structural and referential* integrity:
 * that layers exist, that entities have valid owners, that colours and line
 * types are in range. It does not check that the geometry means anything.
 *
 * Run against a document deliberately built with a zero-length line, a
 * zero-radius circle, an arc with negative radius, a zero-sweep arc, duplicate
 * polyline vertices, zero-height text, coordinates of 10^12 and out-of-range
 * angles, the audit reported **zero errors and zero fixes**.
 *
 * "Zero audit errors" is therefore necessary but nowhere near sufficient. This
 * module is the other half, so the acceptance criterion can read "zero audit
 * errors AND zero geometric validity failures".
 */

export interface ValidityFailure {
  entityId: string;
  rule: string;
  detail: string;
}

/** Coordinates beyond this are rejected as outside any plausible drawing bound. */
export const COORDINATE_BOUND = 1e9;

export function checkGeometricValidity(entities: DrawingEntity[]): ValidityFailure[] {
  const out: ValidityFailure[] = [];
  const fail = (entityId: string, rule: string, detail: string): void => {
    out.push({ entityId, rule, detail });
  };
  const inBound = (n: number): boolean => Number.isFinite(n) && Math.abs(n) <= COORDINATE_BOUND;

  for (const e of entities) {
    switch (e.type) {
      case 'LINE': {
        if (![e.start.x, e.start.y, e.end.x, e.end.y].every(inBound)) {
          fail(e.id, 'coordinate-bound', `coordinate beyond +/-${COORDINATE_BOUND}`);
        } else if (Math.hypot(e.end.x - e.start.x, e.end.y - e.start.y) === 0) {
          fail(e.id, 'zero-length', 'line has identical endpoints');
        }
        break;
      }
      case 'CIRCLE': {
        if (!(e.radius > 0)) fail(e.id, 'non-positive-radius', `radius ${e.radius}`);
        else if (![e.centre.x, e.centre.y].every(inBound)) {
          fail(e.id, 'coordinate-bound', 'centre outside the declared bound');
        }
        break;
      }
      case 'ARC': {
        if (!(e.radius > 0)) fail(e.id, 'non-positive-radius', `radius ${e.radius}`);
        else if (!isNormalised(e.startAngle) || !isNormalised(e.endAngle)) {
          fail(e.id, 'angle-range', 'angle outside [0, 360)');
        } else if (sweepOf(e.startAngle, e.endAngle) === 0) {
          fail(e.id, 'zero-sweep', 'arc start and end angles are equal');
        } else if (![e.centre.x, e.centre.y].every(inBound)) {
          fail(e.id, 'coordinate-bound', 'centre outside the declared bound');
        }
        break;
      }
      case 'LWPOLYLINE': {
        if (e.vertices.length < 2) {
          fail(e.id, 'too-few-vertices', `${e.vertices.length} vertices`);
        } else if (hasDuplicateConsecutive(e.vertices)) {
          fail(e.id, 'duplicate-vertex', 'consecutive vertices are identical');
        } else if (!e.vertices.every((v) => inBound(v.x) && inBound(v.y))) {
          fail(e.id, 'coordinate-bound', 'vertex outside the declared bound');
        }
        break;
      }
      case 'TEXT':
      case 'MTEXT': {
        if (!(e.height > 0)) fail(e.id, 'zero-height-text', `height ${e.height}`);
        else if (![e.anchor.x, e.anchor.y].every(inBound)) {
          fail(e.id, 'coordinate-bound', 'anchor outside the declared bound');
        }
        break;
      }
    }
  }
  return out;
}

const isNormalised = (a: number): boolean => Number.isFinite(a) && a >= 0 && a < 360;

const sweepOf = (s: number, e: number): number => {
  const d = (e - s) % 360;
  return d < 0 ? d + 360 : d;
};

function hasDuplicateConsecutive(vs: readonly { x: number; y: number }[]): boolean {
  for (let i = 1; i < vs.length; i++) {
    const a = vs[i - 1]!;
    const b = vs[i]!;
    if (a.x === b.x && a.y === b.y) return true;
  }
  return false;
}
